import React, {Suspense, useEffect, useRef} from "react";
import { useTranslation } from 'react-i18next';
import {mobileApplicationDevelopment} from "../../constants/meta";
import Meta from "../../components/common/Helmet";
import Loading from "../../components/common/Loading";
import ServicePage from "../../components/sections/ServicePage";
// MobileDevelopment.jsx
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const MobileDevelopment = () => {
  const heroRef = useRef(null);
  const sectionsRef = useRef([]);
  const timelineRef = useRef(null);
  const {t} = useTranslation();
  const lang = (key) => t(`mobileDevelopmentPage.${key}`);

  useEffect(() => {
    // Hero fade-in
    if (heroRef.current) {
      gsap.from(heroRef.current, {
        opacity: 0,
        y: 40,
        duration: 1.2,
        ease: "power3.out",
      });
    }

    // Horizontal timeline pin & scrub
    if (timelineRef.current) {
      const tl = gsap.to(".md-timeline-card", {
        xPercent: -120 * 5, // enough to scroll through many cards
        x: 500, // extra offset for smoother scroll
        ease: "none",
        scrollTrigger: {
          trigger: timelineRef.current,
          pin: true,
          // markers: true,
          scrub: 1,
          start: "0% top",
          end: "95%", // long scroll
        },
      });
      return () => {
        if (tl.scrollTrigger) tl.scrollTrigger.kill();
      };
    }
  }, []);

  const addToSectionsRef = (el) => {
    if (el && !sectionsRef.current.includes(el)) {
      sectionsRef.current.push(el);
    }
  };

  return (
    <div>
      <Meta
        title={mobileApplicationDevelopment.title}
        description={mobileApplicationDevelopment.description}
        canonical={mobileApplicationDevelopment.canonical}
        keywords={mobileApplicationDevelopment.keywords}
        ogTitle={mobileApplicationDevelopment.title}
        ogDescription={mobileApplicationDevelopment.description}
      />
      <Suspense fallback={<Loading />}>
        <ServicePage serviceKey={"mobile"}/>
        <div className="bg-slate-50 text-slate-950">
          {/* HERO SECTION */}
          <section
            ref={heroRef}
            className="relative overflow-hidden mt-10 flex items-center"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-theme-color bg-slate-50 to-indigo-200 pointer-events-none" />
            <div className="absolute -left-40 -top-40 w-80 h-80 bg-indigo-500/10 blur-3xl rounded-full" />
            <div className="absolute -right-40 -bottom-40 w-80 h-80 bg-fuchsia-500/10 blur-3xl rounded-full" />
            <div className="relative z-10 md:w-theme-container w-mobile-container mx-auto py-24 grid lg:grid-cols-2 gap-16">
              <div className="flex flex-col justify-center space-y-8">
      <span className="inline-flex items-center w-fit rounded-full border border-indigo-500/30 bg-slate-900/70 px-4 py-1 text-xs font-medium uppercase tracking-[0.2em] text-indigo-300">
        {lang('mobileDevelopmentStudio')}
      </span>
                <h1 className="text-4xl text-white sm:text-5xl lg:text-6xl font-semibold leading-tight">
                  {lang('weDesignAndBuild')}{" "}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-sky-400 to-fuchsia-400">
          {lang('mobileExperiencesHighlight')}
        </span>{" "}
                  {lang('thatGrowWithYourProduct')}
                </h1>
                <p className="text-slate-300 text-base sm:text-lg max-w-xl">
                  {lang('weAreATechStartup')}
                </p>

                <div className="flex flex-wrap items-center gap-4">
                  <button className="inline-flex items-center justify-center rounded-xl bg-indigo-500 px-6 py-3 text-sm font-medium text-white shadow-lg shadow-indigo-500/30 hover:bg-indigo-400 transition">
                    {lang('bookAFreeDiscoveryCall')}
                  </button>
                  <button className="inline-flex items-center justify-center rounded-xl border border-slate-700 px-6 py-3 text-sm font-medium text-slate-100 hover:bg-slate-900/60 transition">
                    {lang('downloadSampleProposal')}
                  </button>
                </div>

                <div className="flex flex-wrap gap-6 text-xs text-white">
                  <div className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                    {lang('openToMvps')}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-sky-400" />
                    {lang('nativeCrossPlatform')}
                  </div>
                </div>
              </div>

              {/* Illustration */}
              <div className="relative flex items-center justify-center">
                <div className="absolute inset-0 bg-gradient-to-tr from-indigo-500/10 via-sky-500/5 to-fuchsia-500/20 blur-3xl" />
                <div className="relative rounded-3xl border border-slate-800 bg-slate-900/70 p-4 shadow-[0_0_80px_rgba(15,23,42,0.9)]">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="rounded-2xl bg-slate-900/80 border border-slate-800 overflow-hidden">
                      <img
                        src="https://img.freepik.com/free-vector/app-development-illustration_52683-47931.jpg"
                        alt="Mobile app development illustration"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="space-y-4">
                      <div className="rounded-2xl bg-slate-900/80 border border-slate-800 overflow-hidden">
                        <img
                          src="https://img.freepik.com/free-vector/gradient-ux-ui-design-concept_23-2149056152.jpg"
                          alt="UX UI wireframing"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="rounded-2xl bg-slate-900/80 border border-slate-800 overflow-hidden">
                        <img
                          src="https://img.freepik.com/free-vector/development-illustration_52683-47931.jpg"
                          alt="Developers collaborating"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-3 gap-3 text-xs text-slate-300">
                    <div className="rounded-xl bg-slate-900/90 border border-slate-800 px-3 py-2">
                      <p className="font-semibold text-slate-100">{lang('ideaToApp')}</p>
                      <p className="text-[11px] text-slate-400">
                        {lang('strategyUxFlows')}
                      </p>
                    </div>
                    <div className="rounded-xl bg-slate-900/90 border border-slate-800 px-3 py-2">
                      <p className="font-semibold text-slate-100">{lang('engineering')}</p>
                      <p className="text-[11px] text-slate-400">
                        {lang('robustArchitecture')}
                      </p>
                    </div>
                    <div className="rounded-xl bg-slate-900/90 border border-slate-800 px-3 py-2">
                      <p className="font-semibold text-slate-100">{lang('launchAndBeyond')}</p>
                      <p className="text-[11px] text-slate-400">
                        {lang('storeReleaseMonitoring')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="absolute hidden -bottom-6 -right-4 rounded-2xl bg-slate-900/80 border border-slate-700 px-4 py-3 text-xs text-slate-200 shadow-lg shadow-sky-500/20">
                  <p className="font-semibold">{lang('noPortfolioYetFullFocus')}</p>
                  <p className="text-[11px] text-slate-400">
                    {lang('asAnEarlyStageTeam')}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* WHO WE ARE */}
          <section ref={addToSectionsRef} className="py-24 border-t border-slate-900">
            <div className="max-w-5xl mx-auto px-4 lg:px-8 space-y-8">
              <div className="space-y-4 max-w-3xl">
                <h2 className="text-3xl sm:text-4xl font-semibold">
                  {lang('aFocusedMobileTeam')}
                </h2>
                <p className="text-black text-base sm:text-lg">
                  {lang('weAreAYoungEngineering')}
                </p>
                <p className="text-black text-sm sm:text-base">
                  {lang('ourTeamCombinesBackground')}
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
                  <h3 className="text-sm font-semibold text-slate-100 mb-2">
                    {lang('weAcceptAllKindsOfProjects')}
                  </h3>
                  <p className="text-xs text-slate-300">
                    {lang('mvpsRedesignsCompanionApps')}
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
                  <h3 className="text-sm font-semibold text-slate-100 mb-2">
                    {lang('flexibleWithTechnology')}
                  </h3>
                  <p className="text-xs text-slate-300">
                    {lang('weSelectTheRightStack')}
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
                  <h3 className="text-sm font-semibold text-slate-100 mb-2">
                    {lang('transparentAndIterative')}
                  </h3>
                  <p className="text-xs text-slate-300">
                    {lang('weeklyDemosClearMilestones')}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* WHAT WE BUILD */}
          <section
            ref={addToSectionsRef}
            className="py-24 border-t border-slate-900 bg-gradient-to-b from-slate-950 to-slate-900/80"
          >
            <div className="max-w-6xl mx-auto px-4 lg:px-8">
              <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
                <div className="max-w-3xl space-y-3">
                  <h2 className="text-slate-300 text-3xl sm:text-4xl font-semibold">
                    {lang('mobileSolutionsForEveryStage')}
                  </h2>
                  <p className="text-slate-300 text-base sm:text-lg">
                    {lang('whetherYouAreValidating')}
                  </p>
                </div>
                <div className="text-xs text-slate-400 max-w-sm">
                  {lang('fromFirstWhiteboardSketch')}
                </div>
              </div>

              <div className="grid lg:grid-cols-3 gap-6">
                {/* MVP */}
                <div className="rounded-3xl border border-slate-800 bg-slate-950/80 p-6 flex flex-col gap-4">
                  <div className="inline-flex items-center gap-2 text-xs text-emerald-300">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                    {lang('launchReadyInWeeks')}
                  </div>
                  <h3 className="text-slate-300 text-lg font-semibold">{lang('mvpAndZeroToOne')}</h3>
                  <p className="text-sm text-slate-300">
                    {lang('forFoundersAndTeams')}
                  </p>
                  <ul className="text-xs text-slate-400 space-y-2">
                    <li>{lang('rapidDiscovery')}</li>
                    <li>{lang('wireframesAndInteractive')}</li>
                    <li>{lang('developmentOfCoreFlows')}</li>
                    <li>{lang('appStorePlayStoreSetup')}</li>
                  </ul>
                </div>

                {/* PRODUCT GROWTH */}
                <div className="rounded-3xl border border-slate-800 bg-slate-950/80 p-6 flex flex-col gap-4">
                  <div className="inline-flex items-center gap-2 text-xs text-sky-300">
                    <span className="h-1.5 w-1.5 rounded-full bg-sky-400" />
                    {lang('iterateWithoutBreaking')}
                  </div>
                  <h3 className="text-slate-300 text-lg font-semibold">{lang('productGrowthAndV2')}</h3>
                  <p className="text-sm text-slate-300">
                    {lang('forExistingProducts')}
                  </p>
                  <ul className="text-xs text-slate-400 space-y-2">
                    <li>{lang('architectureReview')}</li>
                    <li>{lang('uxRefinement')}</li>
                    <li>{lang('performanceOptimization')}</li>
                    <li>{lang('featureExperiments')}</li>
                  </ul>
                </div>

                {/* INTERNAL & B2B */}
                <div className="rounded-3xl border border-slate-800 bg-slate-950/80 p-6 flex flex-col gap-4">
                  <div className="inline-flex items-center gap-2 text-xs text-fuchsia-300">
                    <span className="h-1.5 w-1.5 rounded-full bg-fuchsia-400" />
                    {lang('toolsThatFitYourWorkflow')}
                  </div>
                  <h3 className="text-slate-300 text-lg font-semibold">{lang('internalAndB2bApps')}</h3>
                  <p className="text-sm text-slate-300">
                    {lang('forTeamsThatNeedCustom')}
                  </p>
                  <ul className="text-xs text-slate-400 space-y-2">
                    <li>{lang('secureAuthentication')}</li>
                    <li>{lang('offlineFirstFlows')}</li>
                    <li>{lang('integrationWithExistingApis')}</li>
                    <li>{lang('enterpriseDeployment')}</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* TECH STACK SECTION */}
          <section ref={addToSectionsRef} className="py-24 border-t border-slate-900">
            <div className="max-w-6xl mx-auto px-4 lg:px-8">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className="space-y-4">
                  <h2 className="text-3xl sm:text-4xl font-semibold">
                    {lang('wePickTheRightTechnology')}
                  </h2>
                  <p className="text-slate-800 text-base sm:text-lg">
                    {lang('weAreComfortableAcross')}
                  </p>
                  <p className="text-slate-800 text-sm sm:text-base">
                    {lang('youDoNotNeedToDecide')}
                  </p>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="rounded-2xl border border-theme-color bg-theme-color/90 p-4">
                    <h3 className="text-slate-300 text-sm font-semibold mb-2">{lang('native')}</h3>
                    <p className="text-xs text-slate-200 mb-3">
                      {lang('forHighestPerformance')}
                    </p>
                    <ul className="text-[11px] text-slate-400 space-y-1.5">
                      <li>{lang('swiftSwiftUiForIos')}</li>
                      <li>{lang('kotlinJetpackCompose')}</li>
                      <li>{lang('advancedDeviceApis')}</li>
                    </ul>
                  </div>

                  <div className="rounded-2xl border border-theme-color bg-theme-color/90 p-4">
                    <h3 className="text-slate-300 text-sm font-semibold mb-2">{lang('crossPlatform')}</h3>
                    <p className="text-xs text-slate-200 mb-3">
                      {lang('forFasterMultiPlatform')}
                    </p>
                    <ul className="text-[11px] text-slate-400 space-y-1.5">
                      <li>{lang('reactNative')}</li>
                      <li>{lang('flutter')}</li>
                      <li>{lang('expoReactNavigation')}</li>
                    </ul>
                  </div>

                  <div className="rounded-2xl border border-theme-color bg-theme-color/90 p-4">
                    <h3 className="text-slate-300 text-sm font-semibold mb-2">{lang('backendAndApis')}</h3>
                    <p className="text-xs text-slate-200 mb-3">
                      {lang('mobileAppsAreOnlyAsStrong')}
                    </p>
                    <ul className="text-[11px] text-slate-400 space-y-1.5">
                      <li>{lang('dotNetNodeDjangoLaravel')}</li>
                      <li>{lang('restGraphqlRealTime')}</li>
                      <li>{lang('cloudAwsGcpAzureSupabase')}</li>
                    </ul>
                  </div>

                  <div className="rounded-2xl border border-theme-color bg-theme-color/90 p-4">
                    <h3 className="text-slate-300 text-sm font-semibold mb-2">{lang('qualityAndDelivery')}</h3>
                    <p className="text-xs text-slate-200 mb-3">
                      {lang('stableBuildsCleanReleases')}
                    </p>
                    <ul className="text-[11px] text-slate-400 space-y-1.5">
                      <li>{lang('automatedTestsCi')}</li>
                      <li>{lang('analyticsCrashReporting')}</li>
                      <li>{lang('versioningReleaseManagement')}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* PROCESS TIMELINE */}
          <section ref={timelineRef} className="py-24 bg-theme-color">
            <div>
              <div className="max-w-6xl mx-auto px-4 lg:px-8 mb-10">
                <h2 className="text-3xl sm:text-4xl font-semibold mb-4 text-white">
                  {lang('fromFirstCallToAppStores')}
                </h2>
                <p className="text-slate-300 text-base sm:text-lg max-w-3xl">
                  {lang('ourProcessIsDesigned')}
                </p>
              </div>
              <div className="relative overflow-hidden">
                <div className="sticky top-0 h-full flex items-center">
                  <div className="flex gap-6 md:gap-10 ml-4 md:ml-16">
                    {[
                      {
                        title: lang('discovery'),
                        subtitle: lang('understandingYourVision'),
                        text: lang('weRunAStructuredConversation'),
                      },
                      {
                        title: lang('productMapping'),
                        subtitle: lang('scopeAndRoadmap'),
                        text: lang('weMapFeatures'),
                      },
                      {
                        title: lang('uxAndPrototyping'),
                        subtitle: lang('flowsAndStructure'),
                        text: lang('weCreateUserFlowsWireframes'),
                      },
                      {
                        title: lang('visualDesign'),
                        subtitle: lang('interfaceAndBrandFit'),
                        text: lang('weCraftAUiSystem'),
                      },
                      {
                        title: lang('engineeringStep'),
                        subtitle: lang('implementationAndIntegration'),
                        text: lang('weBuildTheAppAndBackend'),
                      },
                      {
                        title: lang('qualityAndTesting'),
                        subtitle: lang('stabilityAndEdgeCases'),
                        text: lang('weCombineManualTesting'),
                      },
                      {
                        title: lang('launch'),
                        subtitle: lang('storesAndRollOut'),
                        text: lang('wePrepareStoreListings'),
                      },
                      {
                        title: lang('afterLaunch'),
                        subtitle: lang('iterateWithRealData'),
                        text: lang('weMonitorPerformance'),
                      },
                    ].map((step, idx) => (
                      <article
                        key={idx}
                        className="md-timeline-card min-w-[260px] md:min-w-[320px] max-w-[320px] rounded-3xl border border-slate-800 bg-slate-900/80 p-5 flex flex-col gap-3"
                      >
                        <div className="flex items-center gap-2 text-xs text-slate-400">
                          <span className="h-1.5 w-1.5 rounded-full bg-indigo-400" />
                          {step.subtitle}
                        </div>
                        <h3 className="text-base font-semibold text-slate-50">
                          {step.title}
                        </h3>
                        <p className="text-xs text-slate-300">{step.text}</p>
                        <div className="mt-auto pt-2 text-[11px] text-slate-500">
                          {lang('weeklyCheckInsSharedBacklog')}
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* UX / UI SHOWCASE */}
          <section ref={addToSectionsRef} className="py-24 border-t border-slate-900">
            <div className="max-w-6xl mx-auto px-4 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-4">
                <h2 className="text-3xl sm:text-4xl font-semibold">
                  {lang('uxAndUiThatFeelNatural')}
                </h2>
                <p className="text-black text-base sm:text-lg">
                  {lang('weTreatInterfaceDesign')}
                </p>
                <p className="text-black text-sm sm:text-base">
                  {lang('thatIncludesAccessible')}
                </p>
                <ul className="text-xs text-black space-y-2">
                  <li>{lang('screenMapsAndNavigation')}</li>
                  <li>{lang('designSystemsAndReusable')}</li>
                  <li>{lang('motionDesignForOnBrand')}</li>
                  <li>{lang('accessibilityMindedColor')}</li>
                </ul>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="rounded-3xl border border-slate-800 bg-slate-900/70 p-4 flex flex-col gap-3">
                  <img
                    src="https://img.freepik.com/free-vector/gradient-ui-ux-background_23-2149052117.jpg"
                    alt="Mobile screens UI design"
                    className="rounded-2xl mb-2 object-cover h-40 w-full"
                  />
                  <p className="text-xs text-slate-300">
                    {lang('earlyConceptScreens')}
                  </p>
                </div>
                <div className="rounded-3xl border border-slate-800 bg-slate-900/70 p-4 flex flex-col gap-3">
                  <img
                    src="https://img.freepik.com/free-vector/app-development-illustration_52683-47930.jpg"
                    alt="Designers collaborating"
                    className="rounded-2xl mb-2 object-cover h-40 w-full"
                  />
                  <p className="text-xs text-slate-300">
                    {lang('collaborativeWorkshops')}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* ENGAGEMENT MODELS */}
          <section
            ref={addToSectionsRef}
            className="py-24 border-t border-slate-900 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950"
          >
            <div className="max-w-6xl mx-auto px-4 lg:px-8 space-y-10 text-slate-300">
              <div className="max-w-3xl space-y-3">
                <h2 className="text-3xl sm:text-4xl font-semibold">
                  {lang('engagementsThatFitYourStage')}
                </h2>
                <p className="text-slate-300 text-base sm:text-lg">
                  {lang('becauseWeAreAStartupOurselves')}
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                <div className="rounded-2xl border border-slate-800 bg-slate-900/80 p-5 flex flex-col gap-3">
                  <h3 className="text-sm font-semibold">{lang('fixedScopeMvp')}</h3>
                  <p className="text-xs text-slate-300">
                    {lang('idealIfYouHaveClearIdea')}
                  </p>
                  <ul className="text-[11px] text-slate-400 space-y-1.5">
                    <li>{lang('clearDeliverablesAndMilestones')}</li>
                    <li>{lang('prioritizedBacklogWithNiceToHaves')}</li>
                    <li>{lang('optionalFollowUpSupport')}</li>
                  </ul>
                </div>
                <div className="rounded-2xl border border-slate-800 bg-slate-900/80 p-5 flex flex-col gap-3">
                  <h3 className="text-sm font-semibold">{lang('productPartnershipEngagement')}</h3>
                  <p className="text-xs text-slate-300">
                    {lang('forOngoingMobileProducts')}
                  </p>
                  <ul className="text-[11px] text-slate-400 space-y-1.5">
                    <li>{lang('monthlyOrQuarterlyRoadmaps')}</li>
                    <li>{lang('continuousDiscoveryAndDelivery')}</li>
                    <li>{lang('idealForScaleUps')}</li>
                  </ul>
                </div>
                <div className="rounded-2xl border border-slate-800 bg-slate-900/80 p-5 flex flex-col gap-3">
                  <h3 className="text-sm font-semibold">{lang('productRescueAndAudits')}</h3>
                  <p className="text-xs text-slate-300">
                    {lang('ifYourCurrentApp')}
                  </p>
                  <ul className="text-[11px] text-slate-400 space-y-1.5">
                    <li>{lang('codeQualityAndArchitecture')}</li>
                    <li>{lang('performanceAndReliability')}</li>
                    <li>{lang('roadmapToHealthierCodebase')}</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ / OBJECTIONS */}
          <section ref={addToSectionsRef} className="py-24 border-t border-slate-900">
            <div className="max-w-5xl mx-auto px-4 lg:px-8 space-y-10">
              <div className="space-y-3">
                <h2 className="text-3xl sm:text-4xl font-semibold">
                  {lang('questionsBeforeWeStart')}
                </h2>
                <p className="text-slate-800 text-base sm:text-lg">
                  {lang('weKnowWorkingWithYounger')}
                </p>
              </div>

              <div className="space-y-6">
                <details className="group rounded-2xl border border-theme-color bg-theme-color p-4">
                  <summary className="flex cursor-pointer items-center justify-between text-sm font-medium text-slate-100">
                    {lang('faqWhyTrustYou')}
                    <span className="ml-4 text-xs text-white group-open:rotate-90 transition-transform">
            ▶
          </span>
                  </summary>
                  <div className="mt-3 text-xs text-slate-300">
                    {lang('faqWhyTrustYouAnswer')}
                  </div>
                </details>

                <details className="group rounded-2xl border border-theme-color bg-theme-color p-4">
                  <summary className="flex cursor-pointer items-center justify-between text-sm font-medium text-slate-100">
                    {lang('faqWhichIndustries')}
                    <span className="ml-4 text-xs text-white group-open:rotate-90 transition-transform">
            ▶
          </span>
                  </summary>
                  <div className="mt-3 text-xs text-slate-300">
                    {lang('faqWhichIndustriesAnswer')}
                  </div>
                </details>

                <details className="group rounded-2xl border border-theme-color bg-theme-color p-4">
                  <summary className="flex cursor-pointer items-center justify-between text-sm font-medium text-slate-100">
                    {lang('faqHowCommunicate')}
                    <span className="ml-4 text-xs text-white group-open:rotate-90 transition-transform">
            ▶
          </span>
                  </summary>
                  <div className="mt-3 text-xs text-slate-300">
                    {lang('faqHowCommunicateAnswer')}
                  </div>
                </details>

                <details className="group rounded-2xl border border-theme-color bg-theme-color p-4">
                  <summary className="flex cursor-pointer items-center justify-between text-sm font-medium text-slate-100">
                    {lang('faqWorkWithExistingTeam')}
                    <span className="ml-4 text-xs text-white group-open:rotate-90 transition-transform">
            ▶
          </span>
                  </summary>
                  <div className="mt-3 text-xs text-slate-300">
                    {lang('faqWorkWithExistingTeamAnswer')}
                  </div>
                </details>
              </div>
            </div>
          </section>

          {/* FINAL CTA */}
          <section
            ref={addToSectionsRef}
            className="py-24 bg-gradient-to-r from-theme-color via-zinc-500 to-gray-800"
          >
            <div className="max-w-4xl mx-auto px-4 lg:px-8 text-center space-y-6 text-slate-300">
              <h2 className="text-3xl sm:text-4xl font-semibold">
                {lang('readyToTurnYourMobileIdea')}
              </h2>
              <p className="text-slate-200 text-base sm:text-lg max-w-2xl mx-auto">
                {lang('shareWhereYouAreToday')}
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <button className="inline-flex items-center justify-center rounded-xl bg-theme-color px-7 py-3 text-sm font-medium text-white shadow-lg shadow-indigo-500/40 hover:bg-zinc-400 transition">
                  {lang('scheduleIntroCall')}
                </button>
                <button className="inline-flex items-center justify-center rounded-xl border border-slate-200/40 px-7 py-3 text-sm font-medium text-slate-50 hover:bg-slate-900/60 transition">
                  {lang('sendProjectBrief')}
                </button>
              </div>
              <p className="text-xs text-slate-300">
                {lang('noObligationNoLongForms')}
              </p>
            </div>
          </section>

        </div>
      </Suspense>
    </div>
  );
};

export default MobileDevelopment;
